import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import './TutorialConfiguratiPage.css';

// ── Icone ─────────────────────────────────────────────────────────────────────

const ArticoloIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);
const ModelloIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
const PreconfiguratoIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const ArrowDownIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <polyline points="19 12 12 19 5 12" />
  </svg>
);
const InfoIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function TutorialConfiguratiPage() {
  const navigate = useNavigate();

  return (
    <Layout title="Tutorial configurati">
      <div className="tutconf">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Tutorial — Articoli configurati</h1>
        </div>

        {/* ── Intro ──────────────────────────────────────────────────── */}
        <div className="bop-card">
          <div className="bop-card__body tutconf__intro-body">
            <p className="tutconf__lead">
              In BOP alcuni articoli non si vendono "così come sono", ma vengono
              <strong> composti su misura </strong> mettendo insieme più componenti
              (es. <em>una postazione di lavoro</em> = piano + struttura + accessori).
            </p>
            <p className="tutconf__lead">
              Per gestire questo scenario il gestionale usa <strong>tre entità</strong> che
              lavorano in coppia: l'<strong>articolo padre</strong> (di tipologia
              "Prodotto configurato"), il <strong>modello di configurazione</strong> e
              l'<strong>articolo preconfigurato</strong>. Questo tutorial spiega cosa sono e
              come si legano tra loro.
            </p>
          </div>
        </div>

        {/* ── Le 3 entità ────────────────────────────────────────────── */}
        <h2 className="tutconf__section-title">Le tre entità in gioco</h2>

        <div className="tutconf__cards">

          {/* Card 1: Articolo padre */}
          <div className="bop-card tutconf__entity tutconf__entity--padre">
            <div className="bop-card__body tutconf__entity-body">
              <div className="tutconf__entity-head">
                <div className="tutconf__entity-icon">
                  <ArticoloIcon />
                </div>
                <div className="tutconf__entity-titles">
                  <div className="tutconf__entity-kicker">1. L'articolo padre</div>
                  <div className="tutconf__entity-name">Prodotto configurato</div>
                </div>
              </div>
              <p className="tutconf__entity-desc">
                È un normale articolo dell'anagrafica con tipologia
                <span className="bop-badge bop-badge-info tutconf__badge-inline">PRODOTTO_CONFIGURATO</span>.
                Rappresenta il "contenitore" — ad esempio
                <em> "Postazione di lavoro"</em> — non un componente specifico.
                Non ha un prezzo proprio: il prezzo finale nasce
                dalla somma dei componenti scelti al momento della vendita.
              </p>
              <div className="tutconf__entity-meta">
                <span className="tutconf__meta-label">Dove si crea:</span>
                <button
                  type="button"
                  className="tutconf__link"
                  onClick={() => navigate('/anagrafiche/articoli')}
                >
                  Anagrafiche → Articoli
                </button>
              </div>
              <div className="tutconf__entity-meta">
                <span className="tutconf__meta-label">Esempio:</span>
                <span>Postazione di lavoro · Cucina componibile · PC assemblato</span>
              </div>
            </div>
          </div>

          {/* Card 2: Modello */}
          <div className="bop-card tutconf__entity tutconf__entity--modello">
            <div className="bop-card__body tutconf__entity-body">
              <div className="tutconf__entity-head">
                <div className="tutconf__entity-icon">
                  <ModelloIcon />
                </div>
                <div className="tutconf__entity-titles">
                  <div className="tutconf__entity-kicker">2. Lo schema</div>
                  <div className="tutconf__entity-name">Modello di configurazione</div>
                </div>
              </div>
              <p className="tutconf__entity-desc">
                È una <strong>ricetta riusabile</strong> agganciata a un articolo padre.
                Definisce <strong>quali categorie di componenti</strong> compongono il
                prodotto (es. "piano", "struttura", "accessori"), in che ordine appaiono e
                con quale quantità di default — <strong>ma non sceglie ancora</strong> gli
                articoli specifici. La scelta concreta avviene in preventivo.
              </p>
              <div className="tutconf__entity-meta">
                <span className="tutconf__meta-label">Dove si crea:</span>
                <button
                  type="button"
                  className="tutconf__link"
                  onClick={() => navigate('/archivi/configurazioni/modelli-configurazione')}
                >
                  Archivi → Modelli di configurazione
                </button>
              </div>
              <div className="tutconf__entity-meta">
                <span className="tutconf__meta-label">Esempio:</span>
                <span>Modello "Postazione standard" → 1 piano + 1 struttura + N accessori</span>
              </div>
            </div>
          </div>

          {/* Card 3: Preconfigurato */}
          <div className="bop-card tutconf__entity tutconf__entity--preconf">
            <div className="bop-card__body tutconf__entity-body">
              <div className="tutconf__entity-head">
                <div className="tutconf__entity-icon">
                  <PreconfiguratoIcon />
                </div>
                <div className="tutconf__entity-titles">
                  <div className="tutconf__entity-kicker">3. La configurazione pronta</div>
                  <div className="tutconf__entity-name">Articolo preconfigurato</div>
                </div>
              </div>
              <p className="tutconf__entity-desc">
                È una <strong>configurazione concreta già salvata</strong>: stesso articolo
                padre, ma con gli articoli effettivi <strong>già scelti</strong> (codice, marca,
                modello), le quantità definite e gli sfridi calcolati. Si usa quando una
                certa combinazione si ripete spesso e si vuole inserirla in preventivo
                <strong> con un click</strong>, senza riconfigurare ogni volta.
              </p>
              <div className="tutconf__entity-meta">
                <span className="tutconf__meta-label">Dove si crea:</span>
                <button
                  type="button"
                  className="tutconf__link"
                  onClick={() => navigate('/archivi/configurazioni/articoli-preconfigurati')}
                >
                  Archivi → Articoli preconfigurati
                </button>
              </div>
              <div className="tutconf__entity-meta">
                <span className="tutconf__meta-label">Esempio:</span>
                <span>"Postazione Standard Gold" → piano X1 + struttura Y2 + accessorio Z3</span>
              </div>
            </div>
          </div>

        </div>

        {/* ── Diagramma del legame ──────────────────────────────────── */}
        <h2 className="tutconf__section-title">Come si legano tra loro</h2>

        <div className="bop-card">
          <div className="bop-card__body tutconf__diagram-body">
          <p className="tutconf__diagram-intro">
            L'articolo padre è il <strong>punto di partenza</strong>. Da lui derivano
            sia i modelli che i preconfigurati: ogni modello e ogni preconfigurato
            è "figlio" di un solo articolo padre.
          </p>

          <div className="tutconf__diagram">
            {/* Livello 1: padre */}
            <div className="tutconf__diagram-level">
              <div className="tutconf__node tutconf__node--padre">
                <div className="tutconf__node-kicker">Articolo</div>
                <div className="tutconf__node-title">Postazione di lavoro</div>
                <div className="tutconf__node-tag">tipologia: PRODOTTO_CONFIGURATO</div>
              </div>
            </div>

            {/* Connettore */}
            <div className="tutconf__diagram-connector">
              <div className="tutconf__arrow"><ArrowDownIcon /></div>
              <div className="tutconf__arrow-label">è il padre di…</div>
            </div>

            {/* Livello 2: modello + preconfigurato */}
            <div className="tutconf__diagram-level tutconf__diagram-level--two">
              <div className="tutconf__node tutconf__node--modello">
                <div className="tutconf__node-kicker">Modello</div>
                <div className="tutconf__node-title">"Postazione standard"</div>
                <ul className="tutconf__node-list">
                  <li>slot: <em>piano</em> · qta 1</li>
                  <li>slot: <em>struttura</em> · qta 1</li>
                  <li>slot: <em>accessori</em> · qta 0–N</li>
                </ul>
                <div className="tutconf__node-foot">articoli concreti scelti in preventivo</div>
              </div>

              <div className="tutconf__node tutconf__node--preconf">
                <div className="tutconf__node-kicker">Preconfigurato</div>
                <div className="tutconf__node-title">"Postazione Standard Gold"</div>
                <ul className="tutconf__node-list">
                  <li>piano X1 · qta 1</li>
                  <li>struttura Y2 · qta 1</li>
                  <li>accessorio Z3 · qta 2</li>
                </ul>
                <div className="tutconf__node-foot">articoli già scelti, pronti all'uso</div>
              </div>
            </div>

            {/* Connettore */}
            <div className="tutconf__diagram-connector">
              <div className="tutconf__arrow"><ArrowDownIcon /></div>
              <div className="tutconf__arrow-label">vengono usati in…</div>
            </div>

            {/* Livello 3: preventivo */}
            <div className="tutconf__diagram-level">
              <div className="tutconf__node tutconf__node--preventivo">
                <div className="tutconf__node-kicker">Documento</div>
                <div className="tutconf__node-title">Riga di preventivo / offerta / capitolato</div>
                <div className="tutconf__node-tag">l'utente sceglie modello <strong>oppure</strong> preconfigurato</div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* ── Quando usare cosa ──────────────────────────────────────── */}
        <h2 className="tutconf__section-title">Quando usare il modello e quando il preconfigurato?</h2>

        <div className="bop-card tutconf__compare-card">
          <table className="bop-table tutconf__compare">
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>Modello di configurazione</th>
                <th>Articolo preconfigurato</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Cosa definisce</th>
                <td>Le <strong>categorie</strong> di componenti previste</td>
                <td>Gli <strong>articoli concreti</strong> già scelti</td>
              </tr>
              <tr>
                <th>Articolo specifico</th>
                <td>Va scelto in preventivo, ogni volta</td>
                <td>Già selezionato e salvato</td>
              </tr>
              <tr>
                <th>Quantità</th>
                <td>Default (modificabile)</td>
                <td>Già impostate</td>
              </tr>
              <tr>
                <th>Quando conviene</th>
                <td>Configurazioni <strong>diverse ogni volta</strong> ma con la stessa struttura</td>
                <td>Combinazioni <strong>ricorrenti</strong> che si vendono identiche</td>
              </tr>
              <tr>
                <th>Velocità in preventivo</th>
                <td>Più passaggi (l'operatore sceglie ogni componente)</td>
                <td>Inserimento immediato (un click)</td>
              </tr>
              <tr>
                <th>Riusabile?</th>
                <td>Sì, su tutti i preventivi dello stesso padre</td>
                <td>Sì, su tutti i preventivi dello stesso padre</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── Flusso operativo ───────────────────────────────────────── */}
        <h2 className="tutconf__section-title">Il flusso operativo, passo per passo</h2>

        <div className="bop-card">
          <div className="bop-card__body">
          <ol className="tutconf__steps">
            <li className="tutconf__step">
              <div className="tutconf__step-num">1</div>
              <div className="tutconf__step-body">
                <div className="tutconf__step-title">Crea l'articolo padre</div>
                <div className="tutconf__step-text">
                  Vai in <strong>Anagrafiche → Articoli</strong> e crea (o seleziona) un
                  articolo con tipologia <em>"Prodotto configurato"</em>. È sufficiente
                  per ora indicare codice, descrizione e unità di misura di vendita.
                </div>
              </div>
            </li>
            <li className="tutconf__step">
              <div className="tutconf__step-num">2</div>
              <div className="tutconf__step-body">
                <div className="tutconf__step-title">Definisci uno o più modelli</div>
                <div className="tutconf__step-text">
                  Vai in <strong>Archivi → Modelli di configurazione</strong> e crea un
                  modello agganciato all'articolo padre. Aggiungi le righe componente
                  (categoria merceologica, quantità, marker "principale"/"in descrizione").
                  È il modo più flessibile per configurare in preventivo.
                </div>
              </div>
            </li>
            <li className="tutconf__step">
              <div className="tutconf__step-num">3</div>
              <div className="tutconf__step-body">
                <div className="tutconf__step-title">
                  (Opzionale) Crea un preconfigurato per le combinazioni ricorrenti
                </div>
                <div className="tutconf__step-text">
                  In <strong>Archivi → Articoli preconfigurati</strong> puoi salvare
                  combinazioni "pronte". Volendo, puoi partire da un modello esistente
                  (bottone <em>"Copia da modello"</em>) e poi scegliere gli articoli
                  concreti per ogni componente.
                </div>
              </div>
            </li>
            <li className="tutconf__step">
              <div className="tutconf__step-num">4</div>
              <div className="tutconf__step-body">
                <div className="tutconf__step-title">Usalo in preventivo</div>
                <div className="tutconf__step-text">
                  Quando in una riga di preventivo selezioni un articolo
                  "Prodotto configurato", BOP apre la <em>modale di configurazione</em>
                  della riga e ti permette di scegliere: <strong>un modello</strong> (e
                  poi configuri i componenti) oppure <strong>un preconfigurato</strong>
                  (i componenti vengono copiati subito). Il prezzo della riga viene
                  calcolato sommando i componenti scelti.
                </div>
              </div>
            </li>
          </ol>
          </div>
        </div>

        {/* ── Note utili ─────────────────────────────────────────────── */}
        <div className="bop-card tutconf__notes-card">
          <div className="bop-card__body tutconf__notes-body">
          <div className="tutconf__notes-head">
            <InfoIcon />
            <span>Note utili</span>
          </div>
          <ul className="tutconf__notes-list">
            <li>
              <strong>Lo stesso padre può avere più modelli e più preconfigurati.</strong>
              Tipico: un padre "Postazione di lavoro" con un modello generico e tre
              preconfigurati ("Standard", "Gold", "Premium").
            </li>
            <li>
              <strong>Modello e preconfigurato sono indipendenti.</strong> Un
              preconfigurato non è obbligato a derivare da un modello: il bottone
              "Copia da modello" è solo una scorciatoia di compilazione.
            </li>
            <li>
              <strong>Gli sfridi</strong> (scarti di taglio/lavorazione) sono associati
              al singolo componente del preconfigurato e si trovano in
              <strong> Archivi → Sfridi</strong>. Vengono usati anche dalla modale di
              configurazione riga durante la stesura del preventivo.
            </li>
            <li>
              <strong>Rinominare l'articolo padre</strong> non rinomina automaticamente
              modelli e preconfigurati associati: il loro nome è indipendente e va
              aggiornato a mano se serve.
            </li>
          </ul>
          </div>
        </div>

      </div>
    </Layout>
  );
}
